import { AxiosResponse } from 'axios';

import { IGetSimpleListResponse } from 'models/http/common';
import {
  ICreateDriverShiftsTemplateRequest,
  ICreateDriverShiftsTemplateResponse,
  IDeleteDriverShiftsTemplatesFilters,
  IDeleteDriverShiftsTemplatesResponse,
  IGetDriverShiftsTemplatesFilters,
  IGetDriverShiftsTemplatesResponse,
  IGetSingleDriverShiftsTemplateResponse,
  IUpdateDriverShiftsTemplateRequest,
  IUpdateDriverShiftsTemplateResponse,
} from 'models/http/driver-shifts-templates';
import { IGetHistoryResponse } from 'models/http/history';
import { HttpTimeoutPriority } from 'utils/enums/http-timeout-priority';
import { ModelsNames } from 'utils/enums/model-names';
import { prepareFiltersOfSelection, prepareQPFromFilters, serverAxios } from 'utils/http';

import { HTTP } from './core.http';

class DriverShiftsTemplateHttp implements HTTP<IDeleteDriverShiftsTemplatesFilters> {
  private readonly route: string = ModelsNames.DriverShiftsTemplates.replace(/_/g, '-');

  public async history(id: string, abortController?: AbortController): Promise<IGetHistoryResponse> {
    return serverAxios
      .get(`/${this.route}/${id}/history`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Highest,
      })
      .then((response: AxiosResponse<IGetHistoryResponse>) => response.data);
  }

  public async getSimple(abortController?: AbortController): Promise<IGetSimpleListResponse> {
    return serverAxios
      .get(`/${this.route}/simple`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSimpleListResponse>) => {
        return response.data;
      });
  }

  public async get(
    filters?: IGetDriverShiftsTemplatesFilters,
    abortController?: AbortController,
  ): Promise<IGetDriverShiftsTemplatesResponse> {
    const queryParams = prepareQPFromFilters(filters || {});

    return serverAxios
      .get(`/${this.route}?${queryParams.toString()}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetDriverShiftsTemplatesResponse>) => {
        return response.data;
      });
  }

  public async getById(id: string, abortController?: AbortController): Promise<IGetSingleDriverShiftsTemplateResponse> {
    return serverAxios
      .get(`/${this.route}/single/${id}`, {
        signal: abortController?.signal,
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IGetSingleDriverShiftsTemplateResponse>) => {
        return response.data;
      });
  }

  public async create(data: ICreateDriverShiftsTemplateRequest): Promise<ICreateDriverShiftsTemplateResponse> {
    return serverAxios
      .post(`/${this.route}`, data, { timeout: HttpTimeoutPriority.Medium })
      .then((response: AxiosResponse<ICreateDriverShiftsTemplateResponse>) => {
        return response.data;
      });
  }

  public async update(id: number, data: IUpdateDriverShiftsTemplateRequest): Promise<IUpdateDriverShiftsTemplateResponse> {
    return serverAxios
      .put(`/${this.route}/${id}`, data, {
        timeout: HttpTimeoutPriority.Medium,
      })
      .then((response: AxiosResponse<IUpdateDriverShiftsTemplateResponse>) => {
        return response.data;
      });
  }

  public async delete(ids: number[]): Promise<IDeleteDriverShiftsTemplatesResponse>;
  public async delete(filters: IDeleteDriverShiftsTemplatesFilters): Promise<IDeleteDriverShiftsTemplatesResponse>;
  public async delete(filters: number[] | IDeleteDriverShiftsTemplatesFilters): Promise<IDeleteDriverShiftsTemplatesResponse> {
    return serverAxios
      .delete(`/${this.route}`, {
        data: prepareFiltersOfSelection(filters),
      })
      .then((response: AxiosResponse<IDeleteDriverShiftsTemplatesResponse>) => {
        return response.data;
      });
  }
}

export default DriverShiftsTemplateHttp;
